import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Mail } from 'lucide-react'
import { useState } from 'react'
import { AButton, Callout, Chip, DataTable, Panel, SearchBox, Section } from './Section'
import { cn } from '../../lib/cn'
import { api, get } from '../../lib/api'
import type { AdminStudent } from './types'

/** One row of the "By school" tally. Mirrors `SchoolTally` on the server. */
interface SchoolTally {
  school: string
  /** False for a visitor's own institution. */
  lincoln: boolean
  total: number
  verified: number
  completed: number
  eligible: number
}

/** The student register: searchable, with the certificate re-send from slide 12. */

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Finished 14' },
  { key: 'eligible', label: 'In the draw' },
  /* Beside "Not verified", so the two halves of sign-up read together. */
  { key: 'verified', label: 'Verified' },
  { key: 'unverified', label: 'Not verified' },
  /* Lincoln's own students, and everyone visiting from outside. */
  { key: 'lincoln', label: 'Lincoln' },
  { key: 'outside', label: 'Outside Lincoln' },
  /* Not a filter but a tally — the register counted, not listed. */
  { key: 'schools', label: 'By school' },
] as const

/** The tab that counts rather than lists. */
const BY_SCHOOL = 'schools'

export function StudentsSection() {
  const queryClient = useQueryClient()
  const [term, setTerm] = useState('')
  const [filter, setFilter] = useState<string>('all')
  const [notice, setNotice] = useState<string | null>(null)

  const bySchool = filter === BY_SCHOOL

  const query = useQuery({
    queryKey: ['admin-students', term, filter],
    queryFn: () =>
      get<{ total: number; students: AdminStudent[] }>(
        `/api/admin/students?limit=100&filter=${filter}&q=${encodeURIComponent(term)}`,
        'staff',
      ),
    /* The tally tab has its own query; this one would fetch the whole list. */
    enabled: !bySchool,
  })

  const schools = useQuery({
    queryKey: ['admin-students-by-school'],
    queryFn: () => get<{ schools: SchoolTally[] }>('/api/admin/students/by-school', 'staff'),
    enabled: bySchool,
  })

  const resend = useMutation({
    mutationFn: (id: number) =>
      api<{ certNo: string }>(`/api/admin/certificates/${id}/resend`, { method: 'POST', auth: 'staff' }),
    onSuccess: (res) => {
      setNotice(`Certificate ${res.certNo} re-sent.`)
      queryClient.invalidateQueries({ queryKey: ['admin-students'] })
    },
    onError: () => setNotice('Could not re-send that certificate. Check the mail driver.'),
  })

  const rows = query.data?.students ?? []

  const tally = schools.data?.schools ?? []
  const tallyTotal = tally.reduce((n, s) => n + s.total, 0)
  /** The bar is drawn against the busiest school, not against the total. */
  const biggest = Math.max(...tally.map((s) => s.total), 1)

  return (
    <Section
      title="Students"
      description={
        bySchool
          ? schools.data
            ? `${schools.data.schools.length} schools · ${tallyTotal.toLocaleString()} registered`
            : 'Counting the register…'
          : query.data
            ? `${query.data.total.toLocaleString()} registered${term ? ` · ${rows.length} shown` : ''}`
            : 'Loading the register…'
      }
      /* Nothing to search on the tally — it is already the whole register. */
      actions={
        bySchool ? null : (
          <SearchBox value={term} onChange={setTerm} placeholder="Name, ID, reg. no. or e-mail" />
        )
      }
    >
      <div className="space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                'shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors',
                filter === f.key
                  ? 'border-[#1B2A6B] bg-[#1B2A6B] text-white shadow-md shadow-blue-900/20'
                  : 'border-white bg-white/80 text-slate-600 hover:bg-white',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {notice && <Callout tone={notice.startsWith('Could not') ? 'error' : 'green'}>{notice}</Callout>}

        {bySchool ? (
          <Panel bodyClassName="p-0">
            <DataTable
              rows={tally}
              getKey={(s) => s.school}
              loading={schools.isLoading}
              empty="Nobody has registered yet."
              columns={[
                {
                  key: 'school',
                  label: 'School',
                  mobile: 'title',
                  render: (s) => (
                    <>
                      <span className="block font-bold text-[#0B1E3F] md:max-w-80 md:truncate">
                        {s.school}
                      </span>
                      {/* A visitor's institution, so the figure is not read as Lincoln's. */}
                      {!s.lincoln && (
                        <span className="mt-1 inline-block">
                          <Chip tone="info">outside Lincoln</Chip>
                        </span>
                      )}
                    </>
                  ),
                },
                {
                  key: 'total',
                  label: 'Registered',
                  className: 'whitespace-nowrap',
                  render: (s) => (
                    <span className="flex items-center justify-end gap-2 md:justify-start">
                      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200">
                        <span
                          className="block h-full rounded-full bg-[#1B2A6B]"
                          style={{ width: `${(s.total / biggest) * 100}%` }}
                        />
                      </span>
                      <span className="text-sm font-black tabular-nums text-[#0B1E3F]">
                        {s.total.toLocaleString()}
                      </span>
                    </span>
                  ),
                },
                {
                  key: 'verified',
                  label: 'Verified',
                  align: 'right',
                  className: 'whitespace-nowrap tabular-nums',
                  render: (s) => (
                    <>
                      <span className="font-bold text-[#0B1E3F]">
                        {s.verified.toLocaleString()}
                      </span>
                      {/* What is left is the chase list for that school. */}
                      {s.total > s.verified && (
                        <span className="block text-xs font-semibold text-amber-600">
                          {(s.total - s.verified).toLocaleString()} not verified
                        </span>
                      )}
                    </>
                  ),
                },
                {
                  key: 'progress',
                  label: 'In draw · finished',
                  align: 'right',
                  className: 'whitespace-nowrap tabular-nums',
                  render: (s) => (
                    <span className="font-bold text-[#0B1E3F]">
                      {s.eligible.toLocaleString()}
                      <span className="mx-1 font-normal text-slate-300">·</span>
                      <span className={s.completed > 0 ? 'text-emerald-600' : undefined}>
                        {s.completed.toLocaleString()}
                      </span>
                    </span>
                  ),
                },
              ]}
            />
          </Panel>
        ) : (
        <Panel bodyClassName="p-0">
          {/*
            Each cell is declared once. `mobile` says where it lands on a
            phone, where the row becomes a stacked card rather than six
            columns you have to scroll sideways to read.
          */}
          <DataTable
            rows={rows}
            getKey={(s) => s.id}
            loading={query.isLoading}
            empty={term ? 'No student matches that search.' : 'No students registered yet.'}
            columns={[
              {
                key: 'reg',
                label: 'Reg.',
                mobile: 'lead',
                className: 'whitespace-nowrap',
                render: (s) => (
                  <span className="inline-block rounded-lg bg-[#1B2A6B] px-2 py-1 text-xs font-black tracking-wider tabular-nums text-white">
                    {s.regNo ?? '—'}
                  </span>
                ),
              },
              {
                key: 'student',
                label: 'Student',
                mobile: 'title',
                render: (s) => (
                  <>
                    <span className="block truncate font-bold text-[#0B1E3F] md:max-w-64">
                      {s.name}
                    </span>
                    <span className="block break-all text-xs text-slate-500 md:max-w-64 md:truncate md:break-normal">
                      {/* A visitor has no Student ID — show the code they do have. */}
                      {s.studentId ?? s.regNo ?? '—'} · {s.email}
                    </span>
                    {!s.emailVerified && (
                      <span className="mt-1 inline-block">
                        <Chip tone="critical">not verified</Chip>
                      </span>
                    )}
                  </>
                ),
              },
              {
                key: 'school',
                label: 'School',
                render: (s) => <span className="block md:max-w-48 md:truncate">{s.school}</span>,
              },
              {
                key: 'booths',
                label: 'Booths',
                className: 'whitespace-nowrap',
                render: (s) => (
                  <>
                    <span className="flex items-center justify-end gap-2 md:justify-start">
                      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-slate-200">
                        <span
                          className={cn(
                            'block h-full rounded-full',
                            s.completedAt ? 'bg-emerald-500' : 'bg-orange-400',
                          )}
                          style={{ width: `${Math.min((s.boothsVisited / 14) * 100, 100)}%` }}
                        />
                      </span>
                      <span
                        className={cn(
                          'text-xs font-bold tabular-nums',
                          s.completedAt ? 'text-emerald-600' : 'text-slate-600',
                        )}
                      >
                        {s.boothsVisited}/14
                      </span>
                    </span>
                    {(s.drawEligible || s.hasWonDraw) && (
                      <span className="mt-1 flex justify-end gap-1 md:justify-start">
                        {s.drawEligible && <Chip tone="info">draw</Chip>}
                        {s.hasWonDraw && <Chip tone="attention">won</Chip>}
                      </span>
                    )}
                  </>
                ),
              },
              {
                key: 'cert',
                label: 'Certificate',
                className: 'whitespace-nowrap',
                render: (s) =>
                  s.certNo ? (
                    <>
                      <span className="block font-mono text-xs font-bold text-[#0B1E3F]">
                        {s.certNo}
                      </span>
                      {s.certEmailedAt ? (
                        <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                          <Check className="size-3" strokeWidth={3} /> e-mailed
                        </span>
                      ) : (
                        <span className="mt-0.5 block text-xs font-semibold text-amber-600">
                          not e-mailed
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-300">—</span>
                  ),
              },
              {
                key: 'actions',
                label: '',
                mobile: 'actions',
                align: 'right',
                render: (s) =>
                  s.certNo ? (
                    <AButton
                      size="sm"
                      tone="outline"
                      disabled={resend.isPending}
                      onClick={() => {
                        setNotice(null)
                        resend.mutate(s.id)
                      }}
                    >
                      <Mail className="size-3.5" strokeWidth={2.2} />
                      Re-send
                    </AButton>
                  ) : null,
              },
            ]}
          />
        </Panel>
        )}
      </div>
    </Section>
  )
}
